import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import { ClipboardDocumentIcon, PlusIcon, MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface AccessCode {
  code: string;
  type: string;
  batch_identifier: string | null;
  max_uses: number | null;
  current_uses: number;
  valid_from: string;
  valid_until: string | null;
  access_duration_days: number;
  monthly_discount_coupon_id: string | null;
  annual_discount_coupon_id: string | null;
  metadata: any;
  created_at: string;
  updated_at: string;
}

interface AccessGrant {
  id: string;
  account_id: string;
  access_code_used: string;
  granted_at: string;
  expires_at: string;
  extended_count: number;
  extension_history: any[];
  status: string;
  account?: {
    name: string;
    owner?: {
      email: string;
    };
  };
}

interface StripeCoupon {
  id: string;
  name: string;
  display: string;
  percent_off: number;
  amount_off: number;
  duration: string;
  duration_in_months: number;
  valid: boolean;
  created: number;
}

export default function AccessCodeManagement() {
  const [accessCodes, setAccessCodes] = useState<AccessCode[]>([]);
  const [accessGrants, setAccessGrants] = useState<AccessGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'codes' | 'grants' | 'create' | 'extend' | 'analytics'>('codes');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [stripeCoupons, setStripeCoupons] = useState<StripeCoupon[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [codeUsers, setCodeUsers] = useState<Record<string, any[]>>({});
  
  // Form states
  const [newCode, setNewCode] = useState({
    code: '',
    type: 'beta_test' as 'beta_test' | 'public_beta' | 'enterprise' | 'partner' | 'trial_extension',
    batch_identifier: '',
    max_uses: null as number | null,
    access_duration_days: 30,
    valid_until_days: 180,
    monthly_discount_coupon_id: '',
    annual_discount_coupon_id: '',
    metadata: {}
  });

  const [extendForm, setExtendForm] = useState({
    searchCode: '',
    selectedGrant: null as AccessGrant | null,
    additionalDays: 30,
    reason: ''
  });

  useEffect(() => {
    fetchAccessCodes();
    fetchAccessGrants();
    fetchStripeCoupons();
  }, []);

  const fetchStripeCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const response = await fetch('/api/list-stripe-coupons');
      if (response.ok) {
        const data = await response.json();
        setStripeCoupons(data.coupons || []);
      } else {
        console.error('Failed to fetch Stripe coupons');
      }
    } catch (error) {
      console.error('Error fetching Stripe coupons:', error);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const fetchAccessCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('access_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAccessCodes(data || []);
    } catch (error) {
      console.error('Error fetching access codes:', error);
    }
  };

  const fetchAccessGrants = async () => {
    try {
      // Debug: Check auth status
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Current session when fetching grants:', session?.user?.email, session?.access_token ? 'has token' : 'no token');
      
      const { data, error } = await supabase
        .from('account_access_grants')
        .select('*')
        .order('granted_at', { ascending: false });

      if (error) throw error;
      
      // For now, we don't join with accounts table due to RLS complexity
      // The account name can be fetched separately if needed
      setAccessGrants(data || []);
    } catch (error) {
      console.error('Error fetching access grants:', error);
    } finally {
      setLoading(false);
    }
  };

  const createAccessCode = async () => {
    try {
      const validUntil = newCode.valid_until_days 
        ? new Date(Date.now() + newCode.valid_until_days * 24 * 60 * 60 * 1000).toISOString()
        : null;

      const { error } = await supabase
        .from('access_codes')
        .insert({
          code: newCode.code.toUpperCase(),
          type: newCode.type,
          batch_identifier: newCode.batch_identifier || null,
          max_uses: newCode.max_uses,
          access_duration_days: newCode.access_duration_days,
          valid_until: validUntil,
          monthly_discount_coupon_id: newCode.monthly_discount_coupon_id || null,
          annual_discount_coupon_id: newCode.annual_discount_coupon_id || null,
          metadata: newCode.metadata
        });

      if (error) throw error;

      // Reset form
      setNewCode({
        code: '',
        type: 'beta_test',
        batch_identifier: '',
        max_uses: null,
        access_duration_days: 30,
        valid_until_days: 180,
        monthly_discount_coupon_id: '',
        annual_discount_coupon_id: '',
        metadata: {}
      });

      // Refresh list
      fetchAccessCodes();
      setActiveTab('codes');
    } catch (error) {
      console.error('Error creating access code:', error);
      alert('Failed to create access code');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const fetchUsersForCode = async (code: string) => {
    try {
      const { data, error } = await supabase
        .from('account_access_grants')
        .select(`
          *,
          accounts!inner (
            name,
            owner_id,
            created_at
          )
        `)
        .eq('access_code_used', code);

      if (error) throw error;

      // Fetch user emails for each account
      const usersWithEmails = await Promise.all(
        (data || []).map(async (grant) => {
          const { data: user } = await supabase.auth.admin.getUserById(grant.accounts.owner_id);
          return {
            ...grant,
            email: user?.user?.email || 'Unknown'
          };
        })
      );

      setCodeUsers(prev => ({ ...prev, [code]: usersWithEmails }));
    } catch (error) {
      console.error('Error fetching users for code:', error);
      setCodeUsers(prev => ({ ...prev, [code]: [] }));
    }
  };

  const toggleCodeExpansion = async (code: string) => {
    if (expandedCode === code) {
      setExpandedCode(null);
    } else {
      setExpandedCode(code);
      if (!codeUsers[code]) {
        await fetchUsersForCode(code);
      }
    }
  };

  const searchForGrant = async () => {
    try {
      let query = supabase
        .from('account_access_grants')
        .select('*');

      if (extendForm.searchCode) {
        query = query.eq('access_code_used', extendForm.searchCode.toUpperCase());
      } else {
        alert('Please enter an access code to search');
        return;
      }

      const { data, error } = await query;
      if (error) throw error;

      if (data && data.length > 0) {
        setExtendForm({ ...extendForm, selectedGrant: data[0] });
      } else {
        alert('No grant found');
      }
    } catch (error) {
      console.error('Error searching for grant:', error);
    }
  };

  const extendAccess = async () => {
    if (!extendForm.selectedGrant) return;

    try {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase.rpc('extend_access_grant', {
        p_grant_id: extendForm.selectedGrant.id,
        p_additional_days: extendForm.additionalDays,
        p_extended_by: userData.user?.id,
        p_reason: extendForm.reason
      });

      if (error) throw error;

      alert('Access extended successfully');
      setExtendForm({
        searchCode: '',
        selectedGrant: null,
        additionalDays: 30,
        reason: ''
      });
      fetchAccessGrants();
    } catch (error) {
      console.error('Error extending access:', error);
      alert('Failed to extend access');
    }
  };

  // Analytics calculations
  const analytics = {
    totalCodes: accessCodes.length,
    totalRedemptions: accessCodes.reduce((sum, code) => sum + code.current_uses, 0),
    averageRedemptionRate: accessCodes.length > 0 
      ? (accessCodes.reduce((sum, code) => {
          const maxUses = code.max_uses || 100;
          return sum + (code.current_uses / maxUses);
        }, 0) / accessCodes.length * 100).toFixed(1)
      : 0,
    activeGrants: accessGrants.filter(g => g.status === 'active').length,
    expiredGrants: accessGrants.filter(g => g.status === 'expired').length,
    codesByType: accessCodes.reduce((acc, code) => {
      acc[code.type] = (acc[code.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  };

  const filteredCodes = accessCodes.filter(code => 
    code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.batch_identifier?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Code Management</h2>
        
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('codes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'codes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Access Codes
            </button>
            <button
              onClick={() => setActiveTab('grants')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'grants'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Active Grants
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'create'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Create Code
            </button>
            <button
              onClick={() => setActiveTab('extend')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'extend'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Extend Access
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Analytics
            </button>
          </nav>
        </div>
      </div>

      {/* Access Codes List */}
      {activeTab === 'codes' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search codes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <button
              onClick={fetchAccessCodes}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Refresh
            </button>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type / Batch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uses
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valid Until
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stripe Coupons
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCodes.map((code) => (
                  <React.Fragment key={code.code}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleCodeExpansion(code.code)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          <span className={`mr-2 transition-transform ${expandedCode === code.code ? 'rotate-90' : ''}`}>
                            ▶
                          </span>
                          {code.code}
                        </div>
                      </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          code.type === 'beta_test' ? 'bg-purple-100 text-purple-800' :
                          code.type === 'public_beta' ? 'bg-blue-100 text-blue-800' :
                          code.type === 'enterprise' ? 'bg-green-100 text-green-800' :
                          code.type === 'partner' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {code.type}
                        </span>
                        {code.batch_identifier && (
                          <div className="text-xs text-gray-400 mt-1">{code.batch_identifier}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {code.current_uses} / {code.max_uses || '∞'}
                      {code.max_uses && (
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                          <div 
                            className="bg-blue-600 h-1.5 rounded-full" 
                            style={{ width: `${(code.current_uses / code.max_uses) * 100}%` }}
                          ></div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {code.access_duration_days} days
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {code.valid_until ? new Date(code.valid_until).toLocaleDateString() : 'No expiry'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        {code.monthly_discount_coupon_id && (
                          <div className="text-xs">M: {code.monthly_discount_coupon_id}</div>
                        )}
                        {code.annual_discount_coupon_id && (
                          <div className="text-xs">A: {code.annual_discount_coupon_id}</div>
                        )}
                        {!code.monthly_discount_coupon_id && !code.annual_discount_coupon_id && (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(code.code);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Copy code"
                        >
                          {copiedCode === code.code ? (
                            <span className="text-green-600">✓</span>
                          ) : (
                            <ClipboardDocumentIcon className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded User List */}
                    {expandedCode === code.code && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 bg-gray-50">
                          <div className="ml-6">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              Users who signed up with {code.code}
                            </h4>
                            {codeUsers[code.code] ? (
                              codeUsers[code.code].length > 0 ? (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Signed Up</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {codeUsers[code.code].map((user) => (
                                        <tr key={user.id}>
                                          <td className="px-4 py-2 text-sm text-gray-900">{user.email}</td>
                                          <td className="px-4 py-2 text-sm text-gray-500">{user.accounts?.name || 'N/A'}</td>
                                          <td className="px-4 py-2 text-sm text-gray-500">
                                            {new Date(user.granted_at).toLocaleDateString()}
                                          </td>
                                          <td className="px-4 py-2 text-sm">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                              user.status === 'active' ? 'bg-green-100 text-green-800' :
                                              user.status === 'expired' ? 'bg-red-100 text-red-800' :
                                              user.status === 'converted' ? 'bg-blue-100 text-blue-800' :
                                              'bg-gray-100 text-gray-800'
                                            }`}>
                                              {user.status}
                                            </span>
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-500">
                                            {new Date(user.expires_at).toLocaleDateString()}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 italic">No users have signed up with this code yet.</p>
                              )
                            ) : (
                              <div className="flex items-center text-sm text-gray-500">
                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                                Loading users...
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Active Grants */}
      {activeTab === 'grants' && (
        <div>
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code Used
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Granted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Extensions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accessGrants.map((grant) => (
                  <tr key={grant.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">Account ID: {grant.account_id.slice(0, 8)}...</div>
                        <div className="text-xs text-gray-500">Grant ID: {grant.id.slice(0, 8)}...</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {grant.access_code_used}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(grant.granted_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(grant.expires_at).toLocaleDateString()}
                      <div className="text-xs text-gray-400">
                        ({Math.floor((new Date(grant.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days left)
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        grant.status === 'active' ? 'bg-green-100 text-green-800' :
                        grant.status === 'expired' ? 'bg-red-100 text-red-800' :
                        grant.status === 'revoked' ? 'bg-gray-100 text-gray-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {grant.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {grant.extended_count > 0 ? `${grant.extended_count}x` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Access Code */}
      {activeTab === 'create' && (
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Access Code</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Code</label>
              <input
                type="text"
                value={newCode.code}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter your access code"
              />
              <p className="mt-1 text-sm text-gray-500">Will be converted to uppercase automatically</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Type</label>
              <select
                value={newCode.type}
                onChange={(e) => setNewCode({ ...newCode, type: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="beta_test">Beta Test</option>
                <option value="public_beta">Public Beta</option>
                <option value="enterprise">Enterprise</option>
                <option value="partner">Partner</option>
                <option value="trial_extension">Trial Extension</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Batch Identifier (Optional)</label>
              <input
                type="text"
                value={newCode.batch_identifier}
                onChange={(e) => setNewCode({ ...newCode, batch_identifier: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="e.g., LAUNCH2025, Q1PROMO"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Uses</label>
                <input
                  type="number"
                  value={newCode.max_uses || ''}
                  onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Access Duration (Days)</label>
                <input
                  type="number"
                  value={newCode.access_duration_days}
                  onChange={(e) => setNewCode({ ...newCode, access_duration_days: parseInt(e.target.value) })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Valid For (Days)</label>
              <input
                type="number"
                value={newCode.valid_until_days}
                onChange={(e) => setNewCode({ ...newCode, valid_until_days: parseInt(e.target.value) })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Days from now until code expires"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Monthly Stripe Coupon (Optional)</label>
                <select
                  value={newCode.monthly_discount_coupon_id}
                  onChange={(e) => setNewCode({ ...newCode, monthly_discount_coupon_id: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={loadingCoupons}
                >
                  <option value="">No coupon</option>
                  {stripeCoupons.map(coupon => (
                    <option key={coupon.id} value={coupon.id}>
                      {coupon.display}
                    </option>
                  ))}
                </select>
                {newCode.monthly_discount_coupon_id && (
                  <p className="mt-1 text-xs text-gray-500">Stripe ID: {newCode.monthly_discount_coupon_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Annual Stripe Coupon (Optional)</label>
                <select
                  value={newCode.annual_discount_coupon_id}
                  onChange={(e) => setNewCode({ ...newCode, annual_discount_coupon_id: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={loadingCoupons}
                >
                  <option value="">No coupon</option>
                  {stripeCoupons.map(coupon => (
                    <option key={coupon.id} value={coupon.id}>
                      {coupon.display}
                    </option>
                  ))}
                </select>
                {newCode.annual_discount_coupon_id && (
                  <p className="mt-1 text-xs text-gray-500">Stripe ID: {newCode.annual_discount_coupon_id}</p>
                )}
              </div>
            </div>

            <div className="pt-4">
              <button
                onClick={createAccessCode}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Access Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Access */}
      {activeTab === 'extend' && (
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Extend Beta Access</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Search by Access Code</label>
              <input
                type="text"
                value={extendForm.searchCode}
                onChange={(e) => setExtendForm({ ...extendForm, searchCode: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="BETA-1"
              />
            </div>

            <button
              onClick={searchForGrant}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
              Search
            </button>

            {extendForm.selectedGrant && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Current Grant</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Account ID: {extendForm.selectedGrant.account_id}</p>
                  <p>Code: {extendForm.selectedGrant.access_code_used}</p>
                  <p>Current Expiry: {new Date(extendForm.selectedGrant.expires_at).toLocaleDateString()}</p>
                  <p>Extended: {extendForm.selectedGrant.extended_count} times</p>
                </div>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Additional Days</label>
                    <input
                      type="number"
                      value={extendForm.additionalDays}
                      onChange={(e) => setExtendForm({ ...extendForm, additionalDays: parseInt(e.target.value) })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Reason</label>
                    <textarea
                      value={extendForm.reason}
                      onChange={(e) => setExtendForm({ ...extendForm, reason: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      rows={3}
                      placeholder="Reason for extension..."
                    />
                  </div>

                  <button
                    onClick={extendAccess}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Extend Access
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics */}
      {activeTab === 'analytics' && (
        <div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Codes</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{analytics.totalCodes}</dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Redemptions</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{analytics.totalRedemptions}</dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Avg Redemption Rate</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{analytics.averageRedemptionRate}%</dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Active Grants</dt>
                <dd className="mt-1 text-3xl font-semibold text-green-600">{analytics.activeGrants}</dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Expired Grants</dt>
                <dd className="mt-1 text-3xl font-semibold text-red-600">{analytics.expiredGrants}</dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Conversion Rate</dt>
                <dd className="mt-1 text-3xl font-semibold text-blue-600">
                  {accessGrants.filter(g => g.status === 'converted').length}
                </dd>
              </div>
            </div>
          </div>

          {/* Codes by Type */}
          <div className="mt-8 bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Codes by Type</h3>
            <div className="space-y-2">
              {Object.entries(analytics.codesByType).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{type}</span>
                  <span className="text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}